import gql from "graphql-tag";
import {useMemo} from "react";

import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {activityByContentDescriptorType} from "./__graphql-types__/index.ts";

export const USE_ACTIVITY_QUERY: GqlOperation<
    activityByContentDescriptorType["response"],
    activityByContentDescriptorType["variables"]
> = gqlOp(gql`
    query activityByContentDescriptor(
        $activityID: String!
        $activityKind: String!
    ) {
        learnableContent(id: $activityID, kind: $activityKind) {
            id
            contentId
            contentKind
            topicPaths {
                path {
                    id
                    kind
                    slug
                }
            }
        }
    }
`);

export type Result =
    | {
          status: "loading";
      }
    | {
          status: "no-data";
      }
    | {
          status: "ready";
          domainId: string | null | undefined;
          courseId: string | null | undefined;
          unitId: string | null | undefined;
          lessonId: string | null | undefined;
          contentId: string | null | undefined;
          contentKind: string | null | undefined;
      };

export const useActivityData = (
    activityID: string,
    activityKind: string,
): Result => {
    const [result] = useQuery(USE_ACTIVITY_QUERY, {
        variables: {activityID, activityKind},
        fetchPolicy: FetchPolicy.NetworkOnly,
    });

    const value = useMemo<Result>(() => {
        if (result.status === "loading") {
            return {status: "loading"};
        }

        if (result.status !== "success" || !result.data.learnableContent) {
            return {status: "no-data"};
        }

        const learnableContentDCUL = {
            domainId: "",
            courseId: "",
            unitId: "",
            lessonId: "",
            contentId: result.data.learnableContent.contentId,
            contentKind: result.data.learnableContent.contentKind,
        };
        if (
            !result.data.learnableContent.topicPaths ||
            !result.data.learnableContent.topicPaths.length
        ) {
            return {
                status: "ready",
                ...learnableContentDCUL,
            };
        }

        const topicPath = result.data.learnableContent.topicPaths.find(
            // Find the first path where the course is "ai-activities"
            (topicPath) =>
                !!topicPath &&
                !!topicPath.path &&
                topicPath.path[1]?.slug === "ai-activities",
        );

        if (!topicPath) {
            return {
                status: "ready",
                ...learnableContentDCUL,
            };
        }

        return {
            status: "ready",
            ...learnableContentDCUL,
            domainId: topicPath && topicPath.path && topicPath.path[0]?.id,
            courseId: topicPath && topicPath.path && topicPath.path[1]?.id,
            unitId: topicPath && topicPath.path && topicPath.path[2]?.id,
            lessonId: topicPath && topicPath.path && topicPath.path[3]?.id,
        };
    }, [result]);

    return value;
};
